import { useMemo } from 'react';

import { useSelector } from 'react-redux';

import { isPast, secondsToMilliseconds } from 'date-fns';

import {
    selectCatchupIconValid,
    selectStandardChannelById,
} from 'store/programs/selectors';
import { RBProgram, RBTechnical } from 'store/railsBuilder/reducer.types';
import { RecordingMetadata } from 'store/recordings/reducer.types';
import { ReduxStore } from 'store/types';
import { selectCatchupCapability } from 'store/user/selectors';
import { isCapabilityValid } from 'store/utils';

import findCatchupTechnical from 'services/data-manager/catchup/findCatchupTechnical';
import isCatchupChannel from 'services/data-manager/catchup/isCatchupChannel';
import { VodEditorial } from 'services/data-manager/types';

import getChannelId from 'utils/getChannelId';

function useCatchupDetails(
    details: RBProgram | VodEditorial | RecordingMetadata | undefined,
    endTime: number | undefined,
) {
    const channel = useSelector((state: ReduxStore) =>
        selectStandardChannelById(state, getChannelId(details)),
    );
    const catchupCapability = useSelector(selectCatchupCapability);

    const technicalDetailsCatchup = useMemo(() => {
        const broadcastDetails = details as RBProgram | undefined;
        const editorialDetails = details as VodEditorial | undefined;
        const technicals: RBTechnical[] | undefined =
            broadcastDetails?.editorial?.technicals ??
            editorialDetails?.technicals;

        return findCatchupTechnical(technicals);
    }, [details]);

    const CUEndDateValid = useSelector((state: ReduxStore) =>
        selectCatchupIconValid(
            state,
            endTime,
            technicalDetailsCatchup?.CUEndDate,
        ),
    );

    const isCatchup = useMemo(() => {
        const channelSupportsCatchup = channel && isCatchupChannel(channel);
        const assetHasBeenFinished =
            endTime && isPast(secondsToMilliseconds(endTime));
        const technicalIsValid = technicalDetailsCatchup && CUEndDateValid;

        return !!(
            channelSupportsCatchup &&
            assetHasBeenFinished &&
            technicalIsValid
        );
    }, [channel, technicalDetailsCatchup, endTime, CUEndDateValid]);

    const ableToPlayCatchup = useMemo(
        () => isCatchup && isCapabilityValid(catchupCapability),
        [catchupCapability, isCatchup],
    );

    return { isCatchup, technicalDetailsCatchup, ableToPlayCatchup };
}

export default useCatchupDetails;
